/**
 * Minutes of Meeting (MoM) — list view.
 * Shows past meetings with search, date filter, quick stats, and a report link.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../../lib/api';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Skeleton } from '../../../components/ui/skeleton';
import {
  Search, Plus, FileText, Calendar, MapPin, Users, CheckCircle2, Clock,
  AlertTriangle, BarChart3, Download, ChevronRight, ClipboardList, Filter,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtDate = (s) => {
  if (!s) return '';
  try {
    const d = new Date(s + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return s; }
};

const typeColor = {
  'Management Review': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Weekly Sync': 'bg-sky-50 text-sky-700 border-sky-200',
  'Strategy': 'bg-rose-50 text-rose-700 border-rose-200',
  'Operations': 'bg-amber-50 text-amber-700 border-amber-200',
  'HR Review': 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
  'Finance Review': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Training Review': 'bg-blue-50 text-blue-700 border-blue-200',
  'Ad-hoc': 'bg-slate-100 text-slate-700 border-slate-200',
};

export default function MomListPage() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [types, setTypes] = useState([]);
  const [summary, setSummary] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (search) params.append('search', search);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (typeFilter && typeFilter !== 'all') params.append('meeting_type', typeFilter);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      const r = await api.get(`/mom/meetings?${params}`);
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error('Failed to load meetings');
    } finally { setLoading(false); }
  }, [page, search, dateFrom, dateTo, typeFilter, statusFilter]);

  const loadSummary = useCallback(async () => {
    try {
      const r = await api.get('/mom/report');
      setSummary(r.data.summary);
    } catch { /* ignore */ }
  }, []);

  const loadMeta = useCallback(async () => {
    try {
      const r = await api.get('/mom/meta');
      setTypes(r.data.meeting_types || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSummary(); loadMeta(); }, [loadSummary, loadMeta]);

  const onSeed = async () => {
    try {
      const r = await api.post('/mom/seed');
      if (r.data.skipped) toast.info('Dummy data already present');
      else toast.success(`Seeded ${r.data.inserted} sample meetings`);
      load(); loadSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Seed failed (admin only)');
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const hasFilters = useMemo(
    () => !!(search || dateFrom || dateTo || (typeFilter && typeFilter !== 'all') || (statusFilter && statusFilter !== 'all')),
    [search, dateFrom, dateTo, typeFilter, statusFilter]
  );

  const clearFilters = () => {
    setSearch(''); setSearchInput(''); setDateFrom(''); setDateTo('');
    setTypeFilter('all'); setStatusFilter('all'); setPage(1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 pb-16">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-6 py-5 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-md">
              <ClipboardList className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Minutes of Meeting</h1>
              <p className="text-xs text-slate-500 font-medium mt-0.5">Management decisions, action items & accountability</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => nav('/training/mom/report')} data-testid="mom-report-btn">
              <BarChart3 className="w-4 h-4 mr-1.5" /> Report
            </Button>
            <Button variant="outline" size="sm" onClick={onSeed} data-testid="mom-seed-btn">
              <Download className="w-4 h-4 mr-1.5" /> Load Dummy Data
            </Button>
            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => nav('/training/mom/new')} data-testid="mom-new-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New MoM
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 pt-6">
        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <KpiCard label="Meetings" value={summary?.total_meetings ?? '—'} icon={Calendar} color="indigo" />
          <KpiCard label="Decisions" value={summary?.total_decisions ?? '—'} icon={FileText} color="blue" />
          <KpiCard label="Open / In-Progress" value={summary?.open ?? '—'} icon={Clock} color="amber" />
          <KpiCard label="Completed" value={summary?.done ?? '—'} icon={CheckCircle2} color="emerald" />
          <KpiCard label="Overdue" value={summary?.overdue ?? '—'} icon={AlertTriangle} color="rose" />
        </div>

        {/* Filter bar */}
        <Card className="p-4 mb-4 border-slate-200 shadow-sm">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); setSearch(searchInput); } }}
                placeholder="Search title, agenda, attendee, tag…"
                className="pl-9 h-9 text-sm"
                data-testid="mom-search-input"
              />
            </div>
            <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className="h-9 w-[150px] text-sm" data-testid="mom-date-from" />
            <span className="text-slate-400 text-xs">to</span>
            <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className="h-9 w-[150px] text-sm" data-testid="mom-date-to" />
            <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }}>
              <SelectTrigger className="h-9 w-[180px] text-sm" data-testid="mom-type-filter">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="h-9 w-[170px] text-sm" data-testid="mom-status-filter">
                <SelectValue placeholder="Any decision status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any decision status</SelectItem>
                <SelectItem value="open">Has open</SelectItem>
                <SelectItem value="in_progress">Has in-progress</SelectItem>
                <SelectItem value="done">Has completed</SelectItem>
                <SelectItem value="blocked">Has blocked</SelectItem>
              </SelectContent>
            </Select>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 text-xs" data-testid="mom-clear-filters">
                <Filter className="w-3.5 h-3.5 mr-1" /> Clear
              </Button>
            )}
          </div>
        </Card>

        {/* List */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-28 w-full" />)}
          </div>
        ) : rows.length === 0 ? (
          <Card className="p-12 text-center border-dashed">
            <ClipboardList className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600 font-medium">No meetings found</p>
            <p className="text-xs text-slate-500 mt-1">
              {hasFilters ? 'Try adjusting your filters' : 'Click “Load Dummy Data” to populate sample meetings, or “New MoM” to create one.'}
            </p>
          </Card>
        ) : (
          <div className="space-y-3">
            {rows.map((m) => (
              <Link to={`/training/mom/${m.id}`} key={m.id} className="block group" data-testid={`mom-row-${m.id}`}>
                <Card className="p-4 border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all bg-white">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <Badge variant="outline" className={`text-[10px] font-semibold ${typeColor[m.meeting_type] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                          {m.meeting_type}
                        </Badge>
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> {fmtDate(m.meeting_date)}
                          {m.meeting_time ? <> · {m.meeting_time}</> : null}
                        </span>
                        {m.location && (
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {m.location}
                          </span>
                        )}
                      </div>
                      <h3 className="text-[15px] font-semibold text-slate-900 group-hover:text-indigo-700 transition-colors truncate">
                        {m.title}
                      </h3>
                      {m.summary && <p className="text-xs text-slate-600 mt-1 line-clamp-2 leading-relaxed">{m.summary}</p>}
                      <div className="flex items-center gap-3 mt-2.5 flex-wrap">
                        {m.chairperson && (
                          <span className="text-[11px] text-slate-500">Chair: <span className="font-medium text-slate-700">{m.chairperson}</span></span>
                        )}
                        {m.attendees?.length > 0 && (
                          <span className="text-[11px] text-slate-500 flex items-center gap-1">
                            <Users className="w-3 h-3" /> {m.attendees.length} attendees
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="flex flex-col items-end text-right">
                        <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Decisions</div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-sm font-bold text-slate-800">{m.decision_count || 0}</span>
                          {m.open_count > 0 && (
                            <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">{m.open_count} open</Badge>
                          )}
                          {m.done_count > 0 && (
                            <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">{m.done_count} done</Badge>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-500 transition-colors" />
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > pageSize && (
          <div className="flex items-center justify-between mt-6">
            <div className="text-xs text-slate-500">
              Page {page} of {totalPages} · {total} meetings
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color }) {
  const colors = {
    indigo: 'from-indigo-50 to-violet-50 text-indigo-700 ring-indigo-100',
    blue: 'from-sky-50 to-blue-50 text-sky-700 ring-sky-100',
    amber: 'from-amber-50 to-orange-50 text-amber-700 ring-amber-100',
    emerald: 'from-emerald-50 to-green-50 text-emerald-700 ring-emerald-100',
    rose: 'from-rose-50 to-pink-50 text-rose-700 ring-rose-100',
  };
  return (
    <Card className={`p-4 bg-gradient-to-br ${colors[color]} ring-1 border-0 shadow-sm`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
        </div>
        <Icon className="w-8 h-8 opacity-40" />
      </div>
    </Card>
  );
}
